import { createApp } from "vue";
import PickupStoreFilter from "../components/pickup-store-filter/PickupStoreFilter";
import StoreSelector from "../components/store-selector/StoreSelector";
import storeLocationFilter from "./store-location-filter";
import availabilityFilters from "./availability-filters";

const c = {
  dom: {
    pickupStoreFilterToolbarContainer: "pickupStoreFilterToolbarContainer",
    pickupStoreFilterContainer: "pickupStoreFilterContainer",
    productsGrid: "productsGrid",
    pickupStoreFilter: "js-pickup-store-filter-badge"
  },
  cls: {
    hide: "hide",
    isActive: "is-active",
    isSelected: "is-selected"
  }
};

const $pickupStoreFilterToolbarContainer = document.getElementById(c.dom.pickupStoreFilterToolbarContainer);
const $pickupStoreFilterContainer = document.getElementById(c.dom.pickupStoreFilterContainer);
const $productsGrid = document.getElementById(c.dom.productsGrid);

export const isPickupStoreActivated = () => {
  const $pickupStoreFilters = document.querySelectorAll(`.${c.dom.pickupStoreFilter}`);
  return Array.from($pickupStoreFilters).some(($el) => __.hasClass($el, c.cls.isSelected));
};

function pickupStoreFilterApp() {
  let storeSelectorApp = null;

  /**
   * Mount StoreSelector component if it doesn't exist
   */
  function mountStoreSelectorModal() {
    const existingStoreSelector = document.getElementById("storeSelectorModal");
    if (!existingStoreSelector) {
      // Create container for StoreSelector
      const storeSelectorContainer = document.createElement("div");
      storeSelectorContainer.id = "storeSelectorApp";
      document.body.appendChild(storeSelectorContainer);

      // Mount StoreSelector component in pickup context so its store list is
      // filtered to locations with pickup-eligible products in this collection.
      storeSelectorApp = createApp(StoreSelector, { pickupContext: true });
      storeSelectorApp.mount("#storeSelectorApp");
    }
  }

  /**
   * Step 1: Filter by metafield only — hide non-fulfillable products.
   * Called immediately when "Same Day Pickup" badge is clicked.
   */
  function filterByMetafield() {
    if (!$productsGrid) {
      return;
    }
    const $productWrappers = $productsGrid.querySelectorAll(".product-item__wrapper");
    let visibleProductCount = 0;
    $productWrappers.forEach(($productWrapper) => {
      const $productItem = $productWrapper.querySelector("[data-in-store-fulfillable]") || $productWrapper;
      if ($productItem.dataset.inStoreFulfillable === "true") {
        __.removeClass($productWrapper, c.cls.hide);
        visibleProductCount += 1;
      } else {
        __.addClass($productWrapper, c.cls.hide);
      }
    });
    availabilityFilters.toggleNoResultsMessage(visibleProductCount > 0);

    if (visibleProductCount < 5) {
      window.dispatchEvent(new CustomEvent("requestMoreProducts"));
    }
  }

  /**
   * Step 2: Check stock at selected store.
   * Called after a store is selected. Only runs if a store is chosen.
   */
  function filterProductsByStore() {
    const selectedStore = storeLocationFilter.getSelectedStore();
    if (!selectedStore) {
      return; // No store selected — metafield filter is already applied
    }

    const shopifyStoreId = availabilityFilters.getShopifyStoreIdFromStore(selectedStore);

    if (!$productsGrid) {
      return;
    }

    const $productWrappers = $productsGrid.querySelectorAll(".product-item__wrapper");
    let visibleProductCount = 0;

    $productWrappers.forEach(($productWrapper) => {
      const $productItem = $productWrapper.querySelector("[data-in-store-fulfillable]") || $productWrapper;

      // Skip non-fulfillable (already hidden by metafield filter)
      if ($productItem.dataset.inStoreFulfillable !== "true") {
        return;
      }

      const hasStoreAvailability = $productItem.dataset.hasStoreAvailability === "true";
      if (!hasStoreAvailability) {
        __.addClass($productWrapper, c.cls.hide);
        return;
      }

      try {
        const availabilities = JSON.parse($productItem.dataset.storeAvailabilities);
        let isAvailable = false;

        availabilities.forEach((variantData) => {
          variantData.availabilities.forEach((availability) => {
            if (String(availability.locationId) === shopifyStoreId && availability.available) {
              isAvailable = true;
            }
          });
        });

        const method = isAvailable ? "removeClass" : "addClass";
        __[method]($productWrapper, c.cls.hide);
        if (isAvailable) {
          visibleProductCount += 1;
        }
      } catch (error) {
        console.warn("Error parsing store availability data:", error); // eslint-disable-line no-console
        __.removeClass($productWrapper, c.cls.hide);
        visibleProductCount += 1;
      }
    });

    availabilityFilters.toggleNoResultsMessage(visibleProductCount > 0);
  }

  /**
   * Hide/show Pickup Store filter containers
   */
  function togglePickupStoreFilterVisibility(show) {
    if (!$pickupStoreFilterToolbarContainer || !$pickupStoreFilterContainer) {
      return;
    }
    [$pickupStoreFilterToolbarContainer, $pickupStoreFilterContainer].forEach(($el) => {
      __[show ? "removeClass" : "addClass"]($el, c.cls.hide);
    });
  }

  function init() {
    if (!$pickupStoreFilterToolbarContainer && !$pickupStoreFilterContainer) {
      return; // Don't initialize if metafield is false/blank
    }

    storeLocationFilter.init();

    // Mount StoreSelector component if it doesn't exist
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", mountStoreSelectorModal);
    } else {
      mountStoreSelectorModal();
    }

    // Mount toolbar component (desktop)
    if ($pickupStoreFilterToolbarContainer) {
      const toolbarApp = createApp(PickupStoreFilter);
      toolbarApp.mount("#pickupStoreFilterToolbarContainer");
    }

    // Mount filter sidebar component (mobile)
    if ($pickupStoreFilterContainer) {
      const filterApp = createApp(PickupStoreFilter);
      filterApp.mount("#pickupStoreFilterContainer");
    }

    // Listen for store selection from StoreSelector
    document.addEventListener("storeSelectedForPickup", (event) => {
      if (event.detail && event.detail.store) {
        storeLocationFilter.setSelectedStore(event.detail.store);
        filterProductsByStore();
      }
    });

    // Listen for store location changes
    window.addEventListener("storeLocationChanged", () => {
      filterProductsByStore();
    });

    // Listen for Pickup Store deactivation - hide Pickup Store filter
    window.addEventListener("pickupStoreDeactivated", () => {
      togglePickupStoreFilterVisibility(false);
    });

    // Listen for Pickup Store activation - show Pickup Store filter
    window.addEventListener("pickupStoreActivated", () => {
      togglePickupStoreFilterVisibility(true);
    });

    // Listen for Same Day Pickup badge click — filter by metafield immediately
    window.addEventListener("pickupBadgeActivated", () => {
      filterByMetafield();
    });

    // Listen for Same Day Pickup badge clear — show all products
    window.addEventListener("pickupBadgeDeactivated", () => {
      availabilityFilters.showAllProducts();
    });

    // When new products load via AJAX, re-check stock if a store is selected
    window.addEventListener("productsLoaded", () => {
      if (isPickupStoreActivated() && storeLocationFilter.getSelectedStore()) {
        filterProductsByStore();
      }
    });
  }

  init();

  return {
    filterByMetafield,
    filterProductsByStore
  };
}
export const pickupStoreFilterAppInstance = pickupStoreFilterApp();

export default pickupStoreFilterApp = () => pickupStoreFilterAppInstance;
